import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  BarChart3, 
  Shield, 
  Globe, 
  CreditCard,
  Users,
  Building2,
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  Smartphone,
  FileText,
  ClipboardList,
  TrendingUp,
  Home,
  TreePine,
  Hotel
} from 'lucide-react';

const PROPERTY_TYPES = [
  {
    icon: Hotel,
    title: 'Hoteles',
    description: 'Gestión completa para hoteles de cualquier tamaño',
    image: 'https://images.unsplash.com/photo-1768346564825-6f90c0b89e2e?w=600&q=80'
  },
  {
    icon: Home,
    title: 'Apartamentos',
    description: 'Administra múltiples unidades desde un solo lugar',
    image: 'https://images.unsplash.com/photo-1771749607057-17163af1f0f3?w=600&q=80'
  },
  {
    icon: TreePine,
    title: 'Glamping & Cabañas',
    description: 'Perfecto para alojamientos en la naturaleza',
    image: 'https://images.unsplash.com/photo-1767334573956-68fee24e876b?w=600&q=80'
  },
  {
    icon: Building2,
    title: 'Hostales & Posadas',
    description: 'Control total para alojamientos pequeños y medianos',
    image: 'https://images.unsplash.com/photo-1654482278660-14a8cfd5589d?w=600&q=80'
  }
];

const FEATURES = [
  {
    icon: Calendar,
    title: 'Calendario Unificado',
    description: 'Visualiza todas tus reservas de Booking, Airbnb y más en un solo calendario inteligente.'
  },
  {
    icon: CreditCard,
    title: 'Control de Pagos',
    description: 'Registra pagos parciales y completos. Seguimiento automático de saldos pendientes.'
  },
  {
    icon: BarChart3,
    title: 'Reportes e Informes',
    description: 'Dashboard con métricas clave: ocupación, ingresos reales y rendimiento por habitación.'
  },
  {
    icon: Users,
    title: 'Gestión de Huéspedes',
    description: 'Base de datos de clientes, historial de reservas, check-in y check-out digital.'
  },
  {
    icon: Globe,
    title: 'Channel Manager',
    description: 'Conecta con Booking.com, Airbnb, Expedia y más. Sincronización en tiempo real.'
  },
  {
    icon: Shield,
    title: 'Facturación Electrónica',
    description: 'Cumple con las normativas fiscales locales. Emisión y gestión de facturas automatizadas.'
  },
  {
    icon: Smartphone,
    title: 'Motor de Reservas',
    description: 'Tu propia plataforma de reservas directas, optimizada para convertir visitantes en huéspedes.'
  },
  {
    icon: ClipboardList,
    title: 'Inventario y POS',
    description: 'Control completo de insumos, productos y punto de venta para restaurante y minibar.'
  }
];

const STATS = [
  { value: '+40%', label: 'Aumento en ventas' },
  { value: '100%', label: 'Reducción de procesos manuales' },
  { value: '+200', label: 'Propiedades activas' }
];

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src="https://static.prod-images.emergentagent.com/jobs/66419162-0afe-4996-ab1a-5d519d29062e/images/1419e719ba240b6e00d42093e097a0701e8eac9d9c7b54db9859b4af59d92017.png" alt="Edén" className="h-9 w-9 rounded" />
              <span className="font-bold text-xl text-[#1e293b]" style={{fontFamily: 'Manrope'}}>Edén</span>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              <a href="#soluciones" className="text-sm font-medium text-gray-600 hover:text-[#1e293b] transition-colors">Soluciones</a>
              <a href="#propiedades" className="text-sm font-medium text-gray-600 hover:text-[#1e293b] transition-colors">Propiedades</a>
              <a href="#funcionalidades" className="text-sm font-medium text-gray-600 hover:text-[#1e293b] transition-colors">Funcionalidades</a>
              <a href="#contacto" className="text-sm font-medium text-gray-600 hover:text-[#1e293b] transition-colors">Contacto</a>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#1e293b] font-medium" data-testid="nav-login-btn">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/cotizar">
                <Button className="bg-[#1e293b] hover:bg-[#334155] text-white" data-testid="nav-register-btn">
                  Solicitar Demo
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4 space-y-3">
            <a href="#soluciones" className="block py-2 text-gray-600" onClick={() => setMobileMenuOpen(false)}>Soluciones</a>
            <a href="#propiedades" className="block py-2 text-gray-600" onClick={() => setMobileMenuOpen(false)}>Propiedades</a>
            <a href="#funcionalidades" className="block py-2 text-gray-600" onClick={() => setMobileMenuOpen(false)}>Funcionalidades</a>
            <div className="flex gap-2 pt-2">
              <Link to="/login" className="flex-1">
                <Button variant="outline" className="w-full">Iniciar Sesión</Button>
              </Link>
              <Link to="/cotizar" className="flex-1">
                <Button className="w-full bg-[#1e293b]">Solicitar Demo</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-[#1e293b]" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1761501989065-7c98a5d1f773?w=1600&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="https://static.prod-images.emergentagent.com/jobs/66419162-0afe-4996-ab1a-5d519d29062e/images/1419e719ba240b6e00d42093e097a0701e8eac9d9c7b54db9859b4af59d92017.png" alt="Edén" className="h-16 w-16 rounded-xl shadow-lg" />
                <span className="text-4xl font-extrabold text-white" style={{fontFamily: 'Manrope'}}>Edén</span>
              </div>
              
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                <span className="text-sm text-white/90">Software de gestión hotelera</span>
              </div>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight"
                style={{fontFamily: 'Manrope', fontWeight: 800}}
                data-testid="hero-heading"
              >
                Un ecosistema que <span className="text-[#c9a84c]">transforma</span> la gestión de tu propiedad
              </h1>
              
              <p className="text-base sm:text-lg text-white/80 mb-8 max-w-lg leading-relaxed">
                Optimiza, conecta, gestiona y crece. El software en la nube diseñado para hoteles, 
                apartamentos, glamping y todo tipo de alojamiento.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/cotizar">
                  <Button 
                    size="lg" 
                    className="bg-[#c9a84c] hover:bg-[#b89a40] text-[#1e293b] font-bold text-base px-8 py-6 w-full sm:w-auto"
                    data-testid="hero-cta-btn"
                  >
                    Solicitar Cotización
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="bg-transparent text-white border-white/30 hover:bg-white/10 text-base px-8 py-6 w-full sm:w-auto"
                    data-testid="hero-demo-btn"
                  >
                    Ya tengo cuenta
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden lg:block relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1768346564825-6f90c0b89e2e?w=800&q=80" 
                  alt="Hotel moderno"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e293b]/60 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">+40% ventas</p>
                  <p className="text-xs text-gray-500">Promedio de nuestros clientes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#f8f7f4] border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-3 gap-8 text-center">
            {STATS.map((stat, i) => (
              <div key={i}>
                <p className="text-3xl sm:text-4xl font-extrabold text-[#1e293b]" style={{fontFamily: 'Manrope'}}>{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Soluciones - Feature Cards */}
      <section id="soluciones" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-[#c9a84c] uppercase tracking-wider mb-2">Soluciones</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e293b]" style={{fontFamily: 'Manrope'}}>
              Simplifica, optimiza y crece
            </h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto mt-3">
              Nuestra tecnología ha sido comprobada por cientos de propiedades en toda Latinoamérica
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Calendar, title: 'PMS Completo', desc: 'Gestión hotelera eficiente. Todo lo que tu propiedad necesita en una sola herramienta.' },
              { icon: Globe, title: 'Channel Manager', desc: 'Conéctate a cientos de canales alrededor del mundo expandiendo tu visibilidad.' },
              { icon: Smartphone, title: 'Motor de Reservas', desc: 'Plataforma de reservas directas optimizada para convertir visitantes en huéspedes.' },
              { icon: CreditCard, title: 'Control de Pagos', desc: 'Registra y gestiona todos los pagos con seguimiento automático de saldos.' }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div 
                  key={i}
                  className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-[#1e293b]/20 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-[#1e293b]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#1e293b] transition-colors">
                    <Icon className="h-6 w-6 text-[#1e293b] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e293b] mb-2" style={{fontFamily: 'Manrope'}}>{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  <ChevronRight className="h-5 w-5 text-[#c9a84c] mt-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Property Types */}
      <section id="propiedades" className="py-20 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-[#c9a84c] uppercase tracking-wider mb-2">Tipos de Propiedad</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e293b]" style={{fontFamily: 'Manrope'}}>
              Diseñado para todo tipo de alojamiento
            </h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto mt-3">
              No importa si tienes un hotel, apartamentos turísticos, glamping o cabañas. Nuestro PMS se adapta a ti.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROPERTY_TYPES.map((type, i) => {
              const Icon = type.icon;
              return (
                <div 
                  key={i} 
                  className="group relative rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300"
                  data-testid={`property-type-${i}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={type.image} 
                      alt={type.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-5 w-5 text-[#c9a84c]" />
                      <h3 className="font-bold text-[#1e293b]" style={{fontFamily: 'Manrope'}}>{type.title}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{type.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PMS Feature Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-[#c9a84c] uppercase tracking-wider mb-2">PMS</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e293b] mb-6" style={{fontFamily: 'Manrope'}}>
                Una propiedad organizada es una propiedad rentable
              </h2>
              <p className="text-base text-gray-500 mb-8 leading-relaxed">
                Especializado en optimizar y monitorear el rendimiento de cada aspecto de tu negocio.
                Desde el check-in hasta el check-out, todo automatizado.
              </p>
              
              <div className="space-y-4">
                {[
                  'Control total de reservas en tiempo real',
                  'Check-in y check-out digital',
                  'Registro y seguimiento de pagos parciales',
                  'Reportes de ingresos reales por período',
                  'Gestión de múltiples propiedades',
                  'Interfaz visual de estado de habitaciones'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#1e293b] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/cotizar" className="inline-block mt-8">
                <Button className="bg-[#1e293b] hover:bg-[#334155] text-white px-8">
                  Solicitar Demo
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="bg-[#f8f7f4] rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Calendar, title: 'Calendario dinámico' },
                    { icon: CreditCard, title: 'Pasarelas de pago' },
                    { icon: FileText, title: 'Facturación electrónica' },
                    { icon: BarChart3, title: 'Reportes e informes' },
                    { icon: Users, title: 'Web Check-in' },
                    { icon: ClipboardList, title: 'Inventario y recetas' }
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1e293b]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-[#1e293b]" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{item.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Features Grid */}
      <section id="funcionalidades" className="py-20 bg-[#1e293b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-[#c9a84c] uppercase tracking-wider mb-2">Funcionalidades</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{fontFamily: 'Manrope'}}>
              Todo lo que necesitas en un solo lugar
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={i} 
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-11 h-11 bg-[#c9a84c]/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-[#c9a84c]" />
                  </div>
                  <h3 className="font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>{feature.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Channel Manager Logos */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-[#c9a84c] uppercase tracking-wider mb-2">Channel Manager</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1e293b]" style={{fontFamily: 'Manrope'}}>
              Más visibilidad, más ocupación
            </h2>
            <p className="text-base text-gray-500 mt-2">Conecta con las principales OTAs del mundo</p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            {['Booking.com', 'Airbnb', 'Expedia', 'Google Hotels', 'TripAdvisor'].map((name, i) => (
              <div key={i} className="text-lg sm:text-xl font-bold text-gray-400" style={{fontFamily: 'Manrope'}}>
                {name}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-4 mt-8">
            {[
              'Integra los canales que más venden',
              'Olvídate de sobreventas',
              '100% integrado al PMS',
              'Maneja tarifas con un solo click'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#f8f7f4] rounded-full px-4 py-2">
                <CheckCircle2 className="h-4 w-4 text-[#1e293b]" />
                <span className="text-sm text-gray-600">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contacto" className="py-20 bg-[#f8f7f4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-[#c9a84c] uppercase tracking-wider mb-2">Comienza hoy</p>
          <h2 
            className="text-3xl sm:text-4xl font-bold text-[#1e293b] mb-4"
            style={{fontFamily: 'Manrope', fontWeight: 800}}
          >
            ¿Listo para transformar la gestión de tu propiedad?
          </h2>
          <p className="text-base text-gray-500 mb-8 max-w-2xl mx-auto">
            Únete a cientos de propietarios que ya optimizaron sus operaciones. 
            Solicita una cotización personalizada y descubre cómo podemos ayudarte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cotizar">
              <Button 
                size="lg" 
                className="bg-[#1e293b] hover:bg-[#334155] text-white text-base px-8 py-6"
                data-testid="cta-register-btn"
              >
                Solicitar Cotización
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href="https://wa.me/573053071494?text=Hola%2C%20quiero%20m%C3%A1s%20informaci%C3%B3n%20sobre%20Ed%C3%A9n%20PMS" target="_blank" rel="noopener noreferrer" data-testid="contacto-whatsapp-btn">
              <Button 
                size="lg" 
                variant="outline"
                className="border-[#1e293b] text-[#1e293b] hover:bg-[#1e293b]/5 text-base px-8 py-6"
              >
                Contactar por WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e293b] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="https://static.prod-images.emergentagent.com/jobs/66419162-0afe-4996-ab1a-5d519d29062e/images/1419e719ba240b6e00d42093e097a0701e8eac9d9c7b54db9859b4af59d92017.png" alt="Edén" className="h-8 w-8 rounded" />
                <span className="font-bold text-lg" style={{fontFamily: 'Manrope'}}>Edén</span>
              </div>
              <p className="text-sm text-white/50 leading-relaxed">
                Software integral para optimizar la operación hotelera, 
                aumentar la visibilidad y ventas de tu propiedad.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white/80">Producto</h4>
              <ul className="space-y-2 text-sm text-white/50">
                <li><a href="#soluciones" className="hover:text-white transition-colors">PMS</a></li>
                <li><a href="#soluciones" className="hover:text-white transition-colors">Channel Manager</a></li>
                <li><a href="#soluciones" className="hover:text-white transition-colors">Motor de Reservas</a></li>
                <li><a href="#funcionalidades" className="hover:text-white transition-colors">Funcionalidades</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white/80">Recursos</h4>
              <ul className="space-y-2 text-sm text-white/50">
                <li><Link to="/cotizar" className="hover:text-white transition-colors">Solicitar Cotización</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Iniciar Sesión</Link></li>
                <li><a href="https://wa.me/573053071494?text=Hola%2C%20quiero%20m%C3%A1s%20informaci%C3%B3n%20sobre%20Ed%C3%A9n%20PMS" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Contactar Asesor</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center text-sm text-white/40">
            <p>© 2026 Edén by Connection Admin Hotel. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
